<script lang="ts">
  import type { StoresKey } from "~/stores";
  import { getStoresContext } from "~/stores";
  import { onMount } from "svelte";

  export let request_store_key: StoresKey;
  
  // Feature flags from fragment configuration
  export let showFacebook: boolean = true;
  export let showInstagram: boolean = true;
  export let showTwitter: boolean = false;
  export let showPinterest: boolean = false;
  export let showLinkedin: boolean = false;
  export let showTiktok: boolean = false;
  export let showYoutube: boolean = false;
  
  // Icon size configuration
  export let iconSize: string = 'base';
  
  // Icon sources
  export let facebookIconSrc: string = '';
  export let instagramIconSrc: string = '';
  export let twitterIconSrc: string = '';
  export let linkedinIconSrc: string = '';
  export let pinterestIconSrc: string = '';
  export let tiktokIconSrc: string = '';
  export let youtubeIconSrc: string = '';

  // Default icon paths (not fallbacks, but explicit defaults)
  const DEFAULT_ICONS = {
    facebook: "/theme/demo2025/images/social/facebook.svg",
    instagram: "/theme/demo2025/images/social/instagram.svg",
    twitter: "/theme/demo2025/images/social/twitter.svg",
    linkedin: "/theme/demo2025/images/social/linkedin.svg",
    pinterest: "/theme/demo2025/images/social/pinterest.svg",
    tiktok: "/theme/demo2025/images/social/tiktok.svg",
    youtube: "/theme/demo2025/images/social/youtube.svg"
  };

  const { store } = getStoresContext(request_store_key);

  // Calculate icon size class based on configuration
  let iconSizeClass = '';
  
  function computeIconSizeClass() {
    switch (iconSize) {
      case 'small':
        return 'icon-size-sm';
      case 'large':
        return 'icon-size-lg';
      default:
        return 'icon-size-base';
    }
  }

  /**
   * Handles different types of URL values:
   * - String: returns the string directly
   * - Array: returns the first item if it exists
   * - Other: returns empty string
   */
  function getUrlValue(value: unknown): string {
    if (typeof value === 'string') {
      return value;
    }
    
    if (Array.isArray(value) && value.length > 0) {
      // Get the first item from the array
      return typeof value[0] === 'string' ? value[0] : '';
    }
    
    return '';
  }

  /**
   * Sanitize and format social media URLs
   * - Ensures URL is a string
   * - Converts http:// to https://
   * - Adds https:// if protocol is missing
   * - Handles domain-only URLs (e.g., "facebook.com/page")
   */
  function sanitizeSocialUrl(url: unknown): string {
    // Get usable URL value (handling arrays)
    const urlValue = getUrlValue(url);
    
    // Handle empty values
    if (!urlValue) return "";
    
    // Trim whitespace
    const trimmedUrl = urlValue.trim();
    if (!trimmedUrl) return "";
    
    // Replace http:// with https://
    if (trimmedUrl.startsWith("http://")) {
      const sanitized = trimmedUrl.replace("http://", "https://");
      return sanitized;
    }
    
    // Add https:// if no protocol specified
    if (!trimmedUrl.startsWith("https://")) {
      const sanitized = `https://${trimmedUrl}`;
      return sanitized;
    }
    
    // Already has https://
    return trimmedUrl;
  }

  // Safely get and sanitize URL values
  function safeGetURL(value: unknown): string {
    if (!value) return "";
    try {
      return sanitizeSocialUrl(value);
    } catch (error) {
      console.error('Error formatting URL:', value, error);
      return "";
    }
  }

  function getIconSrc(customSrc: string, defaultSrc: string, iconName: string): string {
    if (customSrc) {
      return customSrc;
    }
    
    console.warn(`StoreSocials: No custom ${iconName} icon provided, using default: ${defaultSrc}`);
    return defaultSrc;
  }

  // Get social media URLs from store data
  $: facebook = showFacebook && $store?.facebookURL ? safeGetURL($store.facebookURL) : "";
  $: instagram = showInstagram && $store?.instagramURL ? safeGetURL($store.instagramURL) : "";
  $: twitter = showTwitter && $store?.twitterURL ? safeGetURL($store.twitterURL) : "";
  $: pinterest = showPinterest && $store?.pinterestURL ? safeGetURL($store.pinterestURL) : "";
  
  // LinkedIn, TikTok, and YouTube aren't in the Store type by default
  // To support future use, we'll leave these enabled but they'll be empty for now
  $: linkedin = showLinkedin ? "" : "";
  $: tiktok = showTiktok ? "" : "";
  $: youtube = showYoutube ? "" : "";

  onMount(() => {
    // Set icon size class once on mount
    iconSizeClass = computeIconSizeClass();
  });
</script>

<div class="social-links">
  {#if facebook}
    <a href={facebook} target="_blank" rel="noopener noreferrer" class="social-link {iconSizeClass}">
      <img 
        src={getIconSrc(facebookIconSrc, DEFAULT_ICONS.facebook, 'Facebook')} 
        alt="Facebook" />
    </a>
  {/if}
  
  {#if instagram}
    <a href={instagram} target="_blank" rel="noopener noreferrer" class="social-link {iconSizeClass}">
      <img 
        src={getIconSrc(instagramIconSrc, DEFAULT_ICONS.instagram, 'Instagram')} 
        alt="Instagram" />
    </a>
  {/if}
  
  {#if twitter}
    <a href={twitter} target="_blank" rel="noopener noreferrer" class="social-link {iconSizeClass}">
      <img src={getIconSrc(twitterIconSrc, DEFAULT_ICONS.twitter, 'Twitter')} alt="Twitter" />
    </a>
  {/if}
  
  {#if pinterest}
    <a href={pinterest} target="_blank" rel="noopener noreferrer" class="social-link {iconSizeClass}">
      <img src={getIconSrc(pinterestIconSrc, DEFAULT_ICONS.pinterest, 'Pinterest')} alt="Pinterest" />
    </a>
  {/if}
  
  {#if linkedin}
    <a href={linkedin} target="_blank" rel="noopener noreferrer" class="social-link {iconSizeClass}">
      <img src={getIconSrc(linkedinIconSrc, DEFAULT_ICONS.linkedin, 'LinkedIn')} alt="LinkedIn" />
    </a>
  {/if}
  
  {#if tiktok}
    <a href={tiktok} target="_blank" rel="noopener noreferrer" class="social-link {iconSizeClass}">
      <img src={getIconSrc(tiktokIconSrc, DEFAULT_ICONS.tiktok, 'TikTok')} alt="TikTok" />
    </a>
  {/if}
  
  {#if youtube}
    <a href={youtube} target="_blank" rel="noopener noreferrer" class="social-link {iconSizeClass}">
      <img src={getIconSrc(youtubeIconSrc, DEFAULT_ICONS.youtube, 'YouTube')} alt="YouTube" />
    </a>
  {/if}
</div>

<style lang="scss">
  /* Define icon size variables */
  :root {
    --icon-base-size: 24px;
    --icon-sm-scale: 0.75;  /* 75% of base size = 18px */
    --icon-lg-scale: 1.33;  /* 133% of base size = 32px */
  }

  .social-links {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    margin: 0;
  }

  .social-link {
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.2s;
    
    &:hover {
      opacity: 0.8;
    }
    
    img {
      object-fit: contain;
    }

    &.icon-size-sm img {
      height: calc(var(--icon-base-size) * var(--icon-sm-scale));
      width: calc(var(--icon-base-size) * var(--icon-sm-scale));
    }

    &.icon-size-base img {
      height: var(--icon-base-size);
      width: var(--icon-base-size);
    }

    &.icon-size-lg img {
      height: calc(var(--icon-base-size) * var(--icon-lg-scale));
      width: calc(var(--icon-base-size) * var(--icon-lg-scale));
    }
  }
</style> 